import * as React from "react";
import { withTranslation, WithTranslation } from "react-i18next";
import { I18N_NS } from "Config/i18n";
import { observer } from "mobx-react";
import { OutlookLayoutSider } from "./Components/OutlookLayoutSider";
import { Route, Routes } from "react-router-dom";
import { EmailTypeStore, MailSortBy } from "../Stores/EmailTypeStore";
import { PrimaryActionsConfig, DropdownActionConfig } from "Components/General/DetailListActions";
import { SearchBarWithActions } from "../Components/SearchBarWithActions";
import { EmailDetails } from "../Shared/EmailDetails";
import debounce from "debounce";
import { viewportHeight } from "utils/viewportHeight";
import { withRouter, WithRouterProps } from "Components/General/WithRouter";
import { projectsDataStore } from "Stores";
import { MAIL_LINK } from "../Communication";

interface OutlookLayoutProps extends WithTranslation, WithRouterProps {
  emailListStore: EmailTypeStore;
  actionsConfig: {
    primaryActions: PrimaryActionsConfig[];
    dropdownActions?: DropdownActionConfig[];
  };
  onSort: (sortBy: MailSortBy, sortOrder: API_MAILPAGINATION_SORTORDER) => void;
  onSelectRow: (keys: string[], emails: API_MailResponse[]) => void;
  selectedRowKeys: string[];
  onSearch: (text: string) => void;
  onLayoutChange: (text: API_USERDATA_LAYOUTTYPE) => void;
  layoutMode: API_USERDATA_LAYOUTTYPE;
  isIntermediate: boolean;
  isSelectedAll: boolean;
  onSelectAll: () => void;
  onRefresh: () => Promise<void>;
  isSearchBarVisible: boolean;
}

interface OutlookLayoutState {
  selectedEmails: { [emailId: string]: API_MailResponse };
  currentlySelectedEmailId: string;
  sidebarWidth: number;
}

export const OutlookLayout = withTranslation(I18N_NS.PROJECT)(
  withRouter(
    observer(
      class OutlookLayoutClass extends React.Component<OutlookLayoutProps, OutlookLayoutState> {
        /** 
        after refactoring of EmailListPage prevProps.emailListStore.data.length and  
        this.props.emailListStore.data.length are same on mount
        so workaround is @property {lastKnownLength}
        */
        lastKnownLength = 0;
        static displayName = "OutlookLayoutClass";
        mainWrap: React.RefObject<HTMLDivElement> = React.createRef();
        detailsWrap: React.RefObject<HTMLDivElement> = React.createRef();
        resizerRef: React.RefObject<HTMLDivElement> = React.createRef();
        sidebarRef: React.RefObject<HTMLDivElement> = React.createRef();
        startingPoint: number = undefined;
        constructor(props: OutlookLayoutProps) {
          super(props);
          const emailId = props.params["*"];

          this.state = {
            selectedEmails: {},
            currentlySelectedEmailId: emailId || "",
            sidebarWidth: 400,
          };
        }

        componentDidUpdate(prevProps: OutlookLayoutProps) {
          let isStateChanged = false;
          const prevPath = prevProps.location?.pathname;
          const currentPath = this.props.location?.pathname;
          const basePath = `/project/${projectsDataStore.projectId}/email/${
            MAIL_LINK[this.props.emailListStore.emailType]
          }`;
          const newState: Partial<OutlookLayoutState> = {};
          if (prevProps.emailListStore.emailType !== this.props.emailListStore.emailType) {
            newState.selectedEmails = {};
            isStateChanged = true;
          }
          if (prevProps.selectedRowKeys.length !== this.props.selectedRowKeys.length) {
            const selectedEmails = {};
            this.props.selectedRowKeys.forEach((emailId) => {
              const email = this.props.emailListStore.data.find((emailItem) => emailItem.id === emailId);
              selectedEmails[emailId] = email ? { ...email } : true;
            });
            newState.selectedEmails = selectedEmails;
            isStateChanged = true;
          }
          if (isStateChanged) {
            this.setState(newState as OutlookLayoutState, this.preselectFirstEmail);
          }
          if (prevPath?.startsWith(basePath + "/") && currentPath === basePath) {
            this.preselectFirstEmail(true);
          }
          // Currently, because of drafts.
          // On the BE, editing is realized by deleting the old one and creating a new draft.
          // Because of this, we need to redirect to the new draft (with a new id).
          // When manually calling the "navigate" function from one email to another
          if (prevProps.params?.["*"] !== this.props.params?.["*"]) {
            this.setState({ currentlySelectedEmailId: this.props.params?.["*"] });
          }
          // preselect first email if new data loaded in store
          // for now this part is not working because we replacing store instance in HOC EmailListPage
          // data.lenght of both values will be always same
          // TODO find solution to resolve this problem
          if (!prevProps.emailListStore.data.length && this.props.emailListStore.data.length) {
            this.preselectFirstEmail();
          }
        }

        componentWillUnmount() {
          window.removeEventListener("resize", debounce(this.handleSetDetailsHeight, 500));
        }

        componentDidMount() {
          // temporary solution
          setTimeout(() => {
            this.preselectFirstEmail();
          }, 1000);

          this.handleSetDetailsHeight();
          window.addEventListener("resize", debounce(this.handleSetDetailsHeight, 500));
          // handle direct email links
          if (this.props.params.id) {
            this.setState({ currentlySelectedEmailId: this.props.params.id });
          }
          if (this.resizerRef.current) {
            this.initResizableSidebar();
          }
          this.lastKnownLength = this.props.emailListStore.data.length;
        }

        initResizableSidebar() {
          const resizerNode = this.resizerRef.current;
          const sidebarBoundaries = this.sidebarRef.current.getBoundingClientRect();
          let { sidebarWidth } = this.state;

          const setResizerPosition = (moveEvent: MouseEvent) => {
            sidebarWidth = moveEvent.pageX - sidebarBoundaries.left;
            resizerNode.style.left = sidebarWidth + "px";
          };

          const setWidthAndTeardown = (mouseUpEvent: MouseEvent) => {
            resizerNode.classList.remove("active");
            // if resizer is beyond layout viewport or width is too small - discard it to smallest possible
            if (mouseUpEvent.pageX < sidebarBoundaries.left || sidebarWidth < 100) {
              sidebarWidth = this.state.sidebarWidth > 100 ? this.state.sidebarWidth : 100;
              resizerNode.style.left = sidebarWidth + "px";
            }
            this.setState({ sidebarWidth });
            window.removeEventListener("mousemove", setResizerPosition);
            window.removeEventListener("mouseup", setWidthAndTeardown);
          };

          resizerNode.addEventListener("mousedown", (e) => {
            resizerNode.classList.add("active");
            e.preventDefault();
            window.addEventListener("mousemove", setResizerPosition);
            window.addEventListener("mouseup", setWidthAndTeardown);
          });
        }

        handleSetDetailsHeight = () => {
          const wrapperNode = this.mainWrap.current;
          const detailsWrapNode = this.mainWrap.current;
          if (wrapperNode && detailsWrapNode) {
            detailsWrapNode.removeAttribute("style");
            const height = viewportHeight(wrapperNode);
            detailsWrapNode.style.height = height + "px";
          }
        };

        preselectFirstEmail(force = false) {
          // when no email was selected - select first one in the list
          if ((!this.state.currentlySelectedEmailId || force) && this.props.emailListStore.data.length) {
            const firstEmail = this.props.emailListStore.data[0];
            if (firstEmail) {
              this.setState({ currentlySelectedEmailId: firstEmail.id }, () =>
                this.props.navigate(`${firstEmail.id}`, { replace: true })
              );
            }
          }
        }

        goToMailDetails = ({ id }) => {
          this.setState({ currentlySelectedEmailId: id }, () => this.props.navigate(`${id}`, { replace: true }));
        };

        handleCheckEmail = (email: API_MailResponse) => {
          const { selectedEmails } = this.state;

          if (selectedEmails[email.id]) {
            delete selectedEmails[email.id];
          } else {
            selectedEmails[email.id] = email;
          }
          this.props.onSelectRow(Object.keys(selectedEmails), Object.values(selectedEmails) as API_MailResponse[]);
        };

        handleInboxSwitch = async (checked: boolean) => {
          const { emailListStore } = this.props;
          await emailListStore.switchProjectInboxes(checked);
          const isEmailExist = emailListStore.data.find((e) => e.id === this.state.currentlySelectedEmailId);
          if (!isEmailExist) {
            this.preselectFirstEmail(true);
          }
        };

        render() {
          const {
            emailListStore,
            actionsConfig,
            onSearch,
            onLayoutChange,
            onSort,
            layoutMode,
            isSearchBarVisible: isVisible,
          } = this.props;
          const { sidebarWidth } = this.state;
          return (
            <>
              <SearchBarWithActions
                isSearchBarVisible={isVisible}
                emailType={emailListStore.emailType}
                projectTopicInbox={emailListStore.projectInboxes}
                onSearch={onSearch}
                accessLevel={emailListStore.projectSpecific ? "project" : "private"}
                actionsConfig={actionsConfig}
                onSort={onSort}
                sortedBy={emailListStore.sortBy}
                sortedOrder={emailListStore.sortOrder}
                onLayoutChange={onLayoutChange}
                layoutMode={layoutMode}
                onRefresh={emailListStore.refresh}
                onSelectAll={this.props.onSelectAll}
                isIntermediate={this.props.isIntermediate}
                isSelectedAll={this.props.isSelectedAll}
                switchProjectInboxes={this.handleInboxSwitch}
              />
              <div className="email-wrapper-layout" ref={this.mainWrap}>
                {/* // TODO: handle resize of sidebar */}
                <div style={{ width: sidebarWidth }} className="email-sider-wrapper" ref={this.sidebarRef}>
                  <div className="email-sider-resizer" ref={this.resizerRef} style={{ left: sidebarWidth }} />
                  <OutlookLayoutSider
                    emails={emailListStore.asArray}
                    onEmailClick={this.goToMailDetails}
                    onEmailCheck={this.handleCheckEmail}
                    selectedEmails={this.state.selectedEmails}
                    currentlySelectedEmailId={this.state.currentlySelectedEmailId}
                    totalEmails={emailListStore.total}
                    loadMore={emailListStore.loadMoreData}
                    loading={emailListStore.state === "LOADING"}
                  />
                </div>
                <Routes>
                  <Route
                    path={`:id`}
                    element={
                      <div className="email-details-v2-wrapper" ref={this.detailsWrap}>
                        <EmailDetails
                          {...actionsConfig}
                          refreshList={this.props.onRefresh}
                          projectSpecific={emailListStore.projectSpecific}
                          type={emailListStore.emailType}
                          updateEmailData={emailListStore.updateEmailData}
                        />
                      </div>
                    }
                  />
                </Routes>
              </div>
            </>
          );
        }
      }
    )
  )
);
